import { webapiApi } from '../../webapiApi';

// =============================================================================
// Response Types (matching OpenAPI spec)
// =============================================================================

export interface UserResponse {
  id: string;
  name: string;
  email: string;
  role?: 'Editor' | 'Admin';
  pictureUrl?: string | null;
}

export interface InvitationResponse {
  id: string;
  inviteeEmail: string;
  role?: 'Editor' | 'Admin';
  expiresAt: string;
}

interface PaginatedResponse<T> {
  items: T[];
  total: number;
  index: number;
  size: number;
}

// =============================================================================
// Query Parameter Types
// =============================================================================

export interface GetUsersParams {
  PageIndex?: number;
  PageSize?: number;
  OrderBy?: string;
}

export interface GetInvitationsParams {
  PageIndex?: number;
  PageSize?: number;
  OrderBy?: string;
}

export interface UpdateUserParams {
  userId: string;
  name: string;
  email: string;
  role: 'Editor' | 'Admin';
  pictureUrl?: string | null;
}

export interface CreateInvitationParams {
  inviteeEmail: string;
  role?: 'Editor' | 'Admin';
}

// =============================================================================
// API Endpoints
// =============================================================================

export const adminApi = webapiApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Users ──────────────────────────────────────────────────────────
    getUsers: builder.query<UserResponse[], GetUsersParams>({
      query: (params) => ({
        url: '/users',
        params,
      }),
      transformResponse: (response: PaginatedResponse<UserResponse>) => response.items,
      providesTags: (result) =>
        result
          ? [
              ...result.map((user) => ({ type: 'User' as const, id: user.id })),
              { type: 'User', id: 'LIST' },
            ]
          : [{ type: 'User', id: 'LIST' }],
    }),

    getUser: builder.query<UserResponse, string>({
      query: (userId) => `/users/${userId}`,
      providesTags: (_result, _error, userId) => [{ type: 'User', id: userId }],
    }),

    updateUser: builder.mutation<UserResponse, UpdateUserParams>({
      query: ({ userId, ...rest }) => ({
        url: `/users/${userId}`,
        method: 'PUT',
        body: { id: userId, ...rest },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'User', id: userId },
        { type: 'User', id: 'LIST' },
      ],
    }),

    removeUser: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/users/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'User', id: 'LIST' }],
    }),

    // ── Invitations ───────────────────────────────────────────────────
    getInvitations: builder.query<InvitationResponse[], GetInvitationsParams>({
      query: (params) => ({
        url: '/invitations',
        params,
      }),
      transformResponse: (response: PaginatedResponse<InvitationResponse>) => response.items,
      providesTags: (result) =>
        result
          ? [
              ...result.map((inv) => ({ type: 'Invitation' as const, id: inv.id })),
              { type: 'Invitation', id: 'LIST' },
            ]
          : [{ type: 'Invitation', id: 'LIST' }],
    }),

    createInvitation: builder.mutation<InvitationResponse, CreateInvitationParams>({
      query: (body) => ({
        url: '/invitations',
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'Invitation', id: 'LIST' }],
    }),

    removeInvitation: builder.mutation<void, string>({
      query: (invitationId) => ({
        url: `/invitations/${invitationId}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Invitation', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useRemoveUserMutation,
  useGetInvitationsQuery,
  useCreateInvitationMutation,
  useRemoveInvitationMutation,
} = adminApi;
